import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPinned, Search, X, Copy, Trash2, FileDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { PROPERTY_TYPES } from "@/lib/propertyTypes";
import { formatApiError } from "@/context/AuthContext";
import DetailFields from "@/components/DetailFields";
import { todayISO, NOTES_PLACEHOLDER, DETAIL_SCHEMAS } from "@/lib/detailSchemas";

// Normalize Turkish name to match dropdown items ("Burgazada Mahallesi" -> "Burgazada")
const normalizeName = (s) => {
  if (!s) return "";
  return s
    .replace(/\s*(Mahallesi|Köyü|İlçesi|Ilcesi|Belediyesi)\s*$/i, "")
    .trim()
    .toLocaleLowerCase("tr");
};
const findByName = (list, name) => {
  if (!name) return null;
  const target = normalizeName(name);
  return list.find((x) => normalizeName(x.name) === target) || null;
};

export default function ExpertiseForm({
  pickedLatLng,
  onEnablePick,
  onCreated,
  onUpdated,
  onCancel,
  onLookupLocation,
  items = [],
  initial = null,
  readOnly = false,
  onEdit,
  onDuplicate,
  onDelete,
}) {
  const isEdit = !!initial?.id;
  const hasInitial = !!initial;
  const [propertyType, setPropertyType] = useState(initial?.property_type || "mesken");
  const [details, setDetails] = useState(initial?.details || {});
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [province, setProvince] = useState(
    initial ? { id: initial.province_id, name: initial.province_name } : null,
  );
  const [district, setDistrict] = useState(
    initial ? { id: initial.district_id, name: initial.district_name } : null,
  );
  const [neighborhood, setNeighborhood] = useState(
    initial && initial.neighborhood_id != null
      ? { id: initial.neighborhood_id, name: initial.neighborhood_name }
      : null,
  );
  const [ada, setAda] = useState(initial?.ada || "");
  const [parsel, setParsel] = useState(initial?.parsel || "");
  const [expertiseDate, setExpertiseDate] = useState(
    initial?.expertise_date || todayISO(),
  );
  const [notes, setNotes] = useState(initial?.notes || "");
  const [reportLoading, setReportLoading] = useState(false);

  const handleReport = async () => {
    setReportLoading(true);
    try {
      const res = await api.get(`/expertises/${initial.id}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ekspertiz-raporu-ada${initial.ada}-parsel${initial.parsel}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF raporu indirildi");
    } catch (e) {
      toast.error("Rapor oluşturulamadı, lütfen tekrar deneyin");
    } finally {
      setReportLoading(false);
    }
  };
  const [loading, setLoading] = useState(false);
  const [looking, setLooking] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  const [pendingAutoFill, setPendingAutoFill] = useState(null); // {district, neighborhood}
  const [loadingProv, setLoadingProv] = useState(false);
  const [loadingDist, setLoadingDist] = useState(false);
  const [loadingNbh, setLoadingNbh] = useState(false);

  // Skip cascade resets on initial mount when editing OR duplicating.
  const skipReset = useRef({
    details: hasInitial,
    district: hasInitial,
    neighborhood: hasInitial,
  });

  useEffect(() => {
    if (skipReset.current.details) {
      skipReset.current.details = false;
      return;
    }
    setDetails({});
  }, [propertyType]);

  useEffect(() => {
    (async () => {
      setLoadingProv(true);
      try {
        const { data } = await api.get("/locations/provinces");
        setProvinces(data.items);
      } catch (e) {
        toast.error("İl listesi yüklenemedi");
      } finally {
        setLoadingProv(false);
      }
    })();
  }, []);

  useEffect(() => {
    setDistricts([]);
    if (!skipReset.current.district) {
      setDistrict(null);
      setNeighborhoods([]);
      setNeighborhood(null);
    } else {
      skipReset.current.district = false;
    }
    if (!province) return;
    (async () => {
      setLoadingDist(true);
      try {
        const { data } = await api.get("/locations/districts", {
          params: { province_id: province.id },
        });
        setDistricts(data.items);
      } catch (e) {
        toast.error("İlçe listesi yüklenemedi");
      } finally {
        setLoadingDist(false);
      }
    })();
  }, [province]);

  useEffect(() => {
    setNeighborhoods([]);
    if (!skipReset.current.neighborhood) {
      setNeighborhood(null);
    } else {
      skipReset.current.neighborhood = false;
    }
    if (!district) return;
    (async () => {
      setLoadingNbh(true);
      try {
        const { data } = await api.get("/locations/neighborhoods", {
          params: { district_id: district.id },
        });
        setNeighborhoods(data.items);
      } catch (e) {
        toast.error("Mahalle listesi yüklenemedi");
      } finally {
        setLoadingNbh(false);
      }
    })();
  }, [district]);

  const handleLookup = async () => {
    if (!province || !district || !neighborhood) {
      toast.error("Şehir, ilçe ve mahalle seçin");
      return;
    }
    if (!ada || !parsel) {
      toast.error("Ada ve parsel numarasını girin");
      return;
    }
    setLooking(true);
    try {
      const { data } = await api.get("/locations/parsel-lookup", {
        params: {
          province: province.name,
          district: district.name,
          neighborhood: neighborhood.name,
          ada,
          parsel,
        },
      });
      onLookupLocation && onLookupLocation({ lat: data.latitude, lng: data.longitude });
      toast.success("Yaklaşık konum haritada işaretlendi", {
        description: "Gerekirse haritaya tıklayarak parseli tam olarak konumlandırın.",
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Konum bulunamadı");
    } finally {
      setLooking(false);
    }
  };

  // Reverse-geocode a picked coordinate and auto-fill cascade selects when empty.
  const autoFillFromCoords = async (latlng) => {
    setAutoFilling(true);
    try {
      const { data } = await api.get("/locations/reverse-geocode", {
        params: { lat: latlng.lat, lng: latlng.lng },
      });
      if (!data.province) {
        toast.info("Konumdan adres alınamadı, elle seçin");
        return;
      }
      if (provinces.length === 0) return;
      const p = findByName(provinces, data.province);
      if (!p) {
        toast.info(`Şehir eşleşmedi: ${data.province}`);
        return;
      }
      setProvince(p);
      setPendingAutoFill({ district: data.district, neighborhood: data.neighborhood });
      // Success: stamp the ref so we don't re-fill on subsequent effect fires for the SAME coords.
      prevPickedRef.current = latlng;
      toast.success("Adres otomatik dolduruldu", {
        description: `${p.name}${data.district ? " / " + data.district : ""}${data.neighborhood ? " / " + data.neighborhood : ""}`,
      });
    } catch (err) {
      // Silent — user can still enter manually
    } finally {
      setAutoFilling(false);
    }
  };

  // Track the coords we LAST auto-filled successfully. The ref is only advanced
  // inside autoFillFromCoords after setProvince succeeds — never here — so a
  // provinces-list resolve mid-flight cannot lock us out of retrying.
  const prevPickedRef = useRef(null);
  useEffect(() => {
    if (!pickedLatLng) return;
    const prev = prevPickedRef.current;
    if (prev && prev.lat === pickedLatLng.lat && prev.lng === pickedLatLng.lng) return;
    // Skip auto-fill only when editing an existing record (never override user's saved values on edit).
    if (isEdit) return;
    // Every fresh map click re-fills the location cascade with reverse-geocoded values.
    autoFillFromCoords(pickedLatLng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedLatLng, provinces]);

  // Apply pending district (fires on districts list change OR on new pendingAutoFill.district)
  useEffect(() => {
    if (!pendingAutoFill?.district) return;
    if (districts.length === 0) return;
    const d = findByName(districts, pendingAutoFill.district);
    if (d) setDistrict(d);
    setPendingAutoFill((prev) => ({ ...prev, district: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districts, pendingAutoFill?.district]);

  // Apply pending neighborhood (fires on neighborhoods list change OR on new pendingAutoFill.neighborhood)
  useEffect(() => {
    if (!pendingAutoFill?.neighborhood) return;
    if (neighborhoods.length === 0) return;
    const n = findByName(neighborhoods, pendingAutoFill.neighborhood);
    if (n) setNeighborhood(n);
    setPendingAutoFill((prev) => ({ ...(prev || {}), neighborhood: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoods, pendingAutoFill?.neighborhood]);

  const submit = async (e) => {
    e.preventDefault();
    if (!province || !district || !neighborhood) {
      toast.error("Şehir, ilçe ve mahalle seçin");
      return;
    }
    if (!pickedLatLng) {
      toast.error("Harita üzerinde konum işaretleyin");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        property_type: propertyType,
        province_id: province.id,
        province_name: province.name,
        district_id: district.id,
        district_name: district.name,
        neighborhood_id: neighborhood.id,
        neighborhood_name: neighborhood.name,
        ada,
        parsel,
        latitude: pickedLatLng.lat,
        longitude: pickedLatLng.lng,
        notes,
        details,
        expertise_date: expertiseDate || null,
      };
      if (isEdit) {
        const { data } = await api.put(`/expertises/${initial.id}`, payload);
        toast.success("Ekspertiz kaydı güncellendi");
        onUpdated && onUpdated(data);
      } else {
        const { data } = await api.post("/expertises", payload);
        toast.success("Ekspertiz kaydı eklendi");
        onCreated && onCreated(data);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex-1 min-h-0 flex flex-col relative" data-testid="expertise-form">
      {/* Floating close (X) button — kırmızı, belirgin */}
      <button
        type="button"
        onClick={onCancel}
        data-testid="cancel-add-btn"
        aria-label="Kapat"
        className="absolute top-2 right-2 z-20 h-9 w-9 grid place-items-center rounded-sm bg-red-600 hover:bg-red-700 text-white shadow-md ring-1 ring-red-700/20"
      >
        <X className="h-5 w-5" strokeWidth={2.5} />
      </button>

      <div className="flex-1 overflow-y-auto px-4 py-3">
      <fieldset disabled={readOnly} className="space-y-3">
        {/* 1-2) Ekspertiz Tarihi + Taşınmaz Türü — yan yana */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="expertise-date" className="text-amber-800 text-[11px]">Ekspertiz Tarihi</Label>
            <Input
              id="expertise-date"
              type="date"
              value={expertiseDate}
              onChange={(e) => setExpertiseDate(e.target.value)}
              className="rounded-sm bg-amber-50 border-amber-300 text-amber-900 focus-visible:ring-amber-400 h-9"
              data-testid="expertise-date-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Taşınmaz Türü</Label>
            <Select value={propertyType} onValueChange={setPropertyType}>
              <SelectTrigger data-testid="property-type-select" className="rounded-sm h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROPERTY_TYPES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full"
                        style={{ background: p.color }}
                      />
                      {p.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Konum + Yapı — her taşınmaz türünde sabit 2-kolon (Yapı yoksa sağ boş kalır, Konum tutarlı kalır) */}
        <div className="grid grid-cols-2 gap-3 items-start">
          {/* Konum Bilgileri — sky renk */}
          <div className="rounded-sm border border-sky-200 bg-sky-50 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="h-3 w-1 rounded-sm bg-sky-500" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-sky-800">
                Konum Bilgileri
              </div>
            </div>

            <div className="rounded-sm bg-white/70 border border-sky-200 px-2.5 py-1.5">
              <p className="text-[10px] text-sky-900 leading-snug">
                <strong>İpucu:</strong> Otomatik doldurmak için harita üzerinde taşınmaza tıklayabilirsiniz — kutucuklar otomatik dolar. Seçim yaparken güncel idari sınırları girin (tapu kaydından farklı olabilir).
              </p>
            </div>

            <Select
              value={province?.id?.toString() || ""}
              onValueChange={(v) => setProvince(provinces.find((p) => p.id.toString() === v))}
              disabled={loadingProv}
            >
              <SelectTrigger data-testid="province-select" className="rounded-sm bg-white h-9 border-sky-200 focus-visible:ring-sky-400">
                <SelectValue placeholder={loadingProv ? "Yükleniyor..." : "Şehir Seçiniz"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {provinces.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={district?.id?.toString() || ""}
              onValueChange={(v) => setDistrict(districts.find((d) => d.id.toString() === v))}
              disabled={!province || loadingDist}
            >
              <SelectTrigger data-testid="district-select" className="rounded-sm bg-white h-9 border-sky-200 focus-visible:ring-sky-400">
                <SelectValue placeholder={!province ? "Önce şehir" : loadingDist ? "…" : "İlçe Seçiniz"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {districts.map((d) => (
                  <SelectItem key={d.id} value={d.id.toString()}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={neighborhood?.id?.toString() || ""}
              onValueChange={(v) => setNeighborhood(neighborhoods.find((n) => n.id.toString() === v))}
              disabled={!district || loadingNbh}
            >
              <SelectTrigger data-testid="neighborhood-select" className="rounded-sm bg-white h-9 border-sky-200 focus-visible:ring-sky-400">
                <SelectValue placeholder={!district ? "Önce ilçe" : loadingNbh ? "…" : "Mahalle Seçiniz"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {neighborhoods.map((n) => (
                  <SelectItem key={n.id} value={n.id.toString()}>
                    {n.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              id="ada"
              required
              value={ada}
              onChange={(e) => setAda(e.target.value)}
              placeholder="Ada"
              data-testid="ada-input"
              className="rounded-sm bg-white h-9 border-sky-200 focus-visible:ring-sky-400"
            />
            <Input
              id="parsel"
              required
              value={parsel}
              onChange={(e) => setParsel(e.target.value)}
              placeholder="Parsel"
              data-testid="parsel-input"
              className="rounded-sm bg-white h-9 border-sky-200 focus-visible:ring-sky-400"
            />

            <div className="grid grid-cols-2 gap-2 pt-1">
              {!readOnly && (
                <>
                  <Button
                    type="button"
                    variant="default"
                    onClick={handleLookup}
                    disabled={looking}
                    className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white text-xs h-9"
                    data-testid="lookup-btn"
                  >
                    {looking ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        <Search className="h-3.5 w-3.5 mr-1.5" />
                        Haritada Bul
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onEnablePick}
                    className="rounded-sm border-sky-300 text-sky-800 hover:bg-sky-100 text-xs h-9 bg-white"
                    data-testid="pick-location-btn"
                  >
                    <MapPinned className="h-3.5 w-3.5 mr-1.5" />
                    Manuel Seç
                  </Button>
                </>
              )}
            </div>

            <div className="text-[10px] text-sky-900 font-mono flex items-center gap-2" data-testid="picked-coords">
              {pickedLatLng
                ? `${pickedLatLng.lat.toFixed(5)}, ${pickedLatLng.lng.toFixed(5)}`
                : "Koordinat seçilmedi"}
              {autoFilling && (
                <span className="inline-flex items-center gap-1 text-blue-600">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>adres alınıyor…</span>
                </span>
              )}
            </div>
          </div>

          {/* Yapı Bilgileri — DetailFields structural (violet). Yoksa boş kalır. */}
          <div className="min-w-0">
            <DetailFields
              propertyType={propertyType}
              details={details}
              onChange={setDetails}
              filter="structural"
            />
          </div>
        </div>

        {/* 5) Açıklama */}
        <div className="space-y-1">
          <Label htmlFor="notes" className="text-[11px]">Açıklama</Label>
          <Textarea
            id="notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={NOTES_PLACEHOLDER}
            data-testid="notes-input"
            className="rounded-sm resize-y min-h-[70px] text-sm"
          />
        </div>

        {/* 6) Alan ve Değer bilgileri (en altta) */}
        <DetailFields
          propertyType={propertyType}
          details={details}
          onChange={setDetails}
          filter="areaValue"
        />
      </fieldset>
      </div>

      <div className="border-t border-zinc-200 px-5 py-2.5 flex items-center gap-2 shrink-0 bg-white">
        {readOnly ? (
          <>
            <Button
              type="button"
              onClick={() => onEdit && onEdit(initial)}
              className="flex-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-9"
              data-testid="detail-edit-btn"
            >
              Güncelle
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleReport}
              disabled={reportLoading}
              className="rounded-sm border-sky-200 text-sky-700 hover:bg-sky-50 px-3 h-9"
              data-testid="detail-report-btn"
              aria-label="PDF rapor indir"
              title="PDF rapor indir"
            >
              <FileDown className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">{reportLoading ? "Hazırlanıyor..." : "Rapor"}</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onDuplicate && onDuplicate(initial)}
              className="rounded-sm px-3 h-9"
              data-testid="detail-duplicate-btn"
              aria-label="Bu kayıttan çoğalt"
              title="Bu kayıttan çoğalt"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onDelete && onDelete(initial?.id)}
              className="rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 px-3 h-9"
              data-testid="detail-delete-btn"
              aria-label="Kaydı sil"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              className="flex-1 rounded-sm border-zinc-300 h-9"
              onClick={onCancel}
            >
              İptal
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-9"
              data-testid="submit-expertise-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Güncelle" : "Kaydet"}
            </Button>
          </>
        )}
      </div>
    </form>
  );
}
